import React, { useEffect, useMemo, useState } from 'react'
import { withRouter, useParams, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import { getSignupURL, setLocale } from 'utils/localstorage'
import { identify } from 'utils/segmentTracking'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { Box, Container, LinearProgress } from '@mui/material'
import ErrorMessage from 'components/ErrorMessage'
import * as Sentry from '@sentry/react'
import { auth as authModule, clubs as clubsModule } from 'redux/modules'

// @todo: do not show Userline on this page

const LoginWithSocialSites = ({
  authState,
  dispatchSocialLogin,
  dispatchClearAuthState,
  history,
  fetchAuthenticatedUser,
  fetchClubs,
  createUserClub,
  setAuthTokens,
}) => {
  const { provider } = useParams()
  const { search } = useLocation()
   const [loading, setLoading] = useState(true)

  const query = useMemo(() => {
    return new URLSearchParams(search)
  }, [search])

  const [error, setError] = useState(null)

  useEffect(() => {
    const providers = ['google', 'facebook']

    const handleSocialLogin = async () => {
      try {
        setError(null)
        setLoading(true)

        if (!providers.includes(provider)) {
          history.push('/')

          return
        }

        const { response } = await dispatchSocialLogin({
          provider,
          locale: authState.locale,
          code: query.get('code'),
          scope: query.get('scope'),
          utm_source: query.get('utm_source'),
          utm_medium: query.get('utm_medium'),
          utm_campaign: query.get('utm_campaign'),
          utm_term: query.get('utm_term'),
          utm_content: query.get('utm_content'),
        })

        const { access_token: accessToken, refresh_token: refreshToken } =
          response

        setAuthTokens({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        const resUser = await fetchAuthenticatedUser()
        const userData = resUser.response.data
        identify(userData.uuid)
        setLocale(userData.language)

        const clubsRes = await fetchClubs()
        const userClubs = clubsRes.response.data

        if (userClubs.length > 0) {
          history.push('/')
        } else {
          await createUserClub()
          history.push(`${getSignupURL()}/step2`)
        }
      } catch (err) {
        err.sentryEventId = Sentry.captureException(err)
        console.error('Login using socials error:', err)
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    handleSocialLogin()
  }, [
    provider,
    dispatchClearAuthState,
    fetchAuthenticatedUser,
    fetchClubs,
    history,
    createUserClub,
    query,
  ])

  return (
    <Container maxWidth="sm">
      <Box sx={{ m: '2rem' }}>
        {loading && <LinearProgress />}
        {error && <ErrorMessage error={error} />}
      </Box>
    </Container>
  )
}

LoginWithSocialSites.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  dispatchSocialLogin: PropTypes.func.isRequired,
  dispatchClearAuthState: PropTypes.func.isRequired,
  authState: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
  }).isRequired,
  fetchAuthenticatedUser: PropTypes.func.isRequired,
  fetchClubs: PropTypes.func.isRequired,
  createUserClub: PropTypes.func.isRequired,
  setAuthTokens: PropTypes.func.isRequired,
}

export default compose(
  withRouter,
  connect(
    ({ auth }) => ({
      authState: auth,
    }),
    {
      dispatchSocialLogin: authModule.loginWithSocial,
      dispatchClearAuthState: authModule.clearAuthState,
      fetchClubs: clubsModule.fetchClubs,
      createUserClub: clubsModule.createUserClub,
      fetchAuthenticatedUser: authModule.fetchUser,
      setAuthTokens: authModule.setAuthTokens,
    }
  )
)(LoginWithSocialSites)
